import { z } from 'zod';
import { baseComponentSchema, imageCropSchema, buttonSchema } from '../component-base.schemas';

// ImageCard schemas
export const imageCardItemSchema = z.object({
  id: z.string().uuid('Invalid image card item ID'),
  imageUrl: z.string().url('Invalid image URL').optional(),
  cloudfrontUrl: z.string().url('Invalid CloudFront URL').optional(),
  alt: z.string().max(200, 'Alt text must be less than 200 characters').optional(),
  title: z.string().max(100, 'Title must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
  url: z.string().optional(), // Link URL for card
  target: z.enum(['_self', '_blank']).default('_self'),
  button: z
    .object({
      text: z.string().max(30, 'Button text must be less than 30 characters'),
      style: z.enum(['primary', 'secondary', 'outline', 'ghost']),
    })
    .optional(),
  order: z.number().int().min(0).default(0),
  // Image crop data
  crop: imageCropSchema,
});

export const imageCardCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    overlayColor: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    title: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    description: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const imageCardDataSchema = z.object({
  title: z.string().max(200, 'Title must be less than 200 characters').optional(),
  subtitle: z.string().max(500, 'Subtitle must be less than 500 characters').optional(),
  showTitle: z.boolean().default(false),
  showSubtitle: z.boolean().default(false),
  items: z.array(imageCardItemSchema).min(1, 'At least one image card item is required'),
  columns: z.number().int().min(1).max(4).default(3),
  aspectRatio: z.enum(['square', 'portrait', 'landscape']).default('square'),
  overlayStyle: z.enum(['none', 'light', 'dark', 'gradient']).default('dark'),
  overlayOpacity: z.number().min(0).max(100).default(50),
  textPosition: z.enum(['top', 'center', 'bottom']).default('center'),
  spacing: z.enum(['none', 'small', 'normal', 'large']).default('normal'),
  customColors: imageCardCustomColorsSchema,
  // Optional call-to-action buttons
  buttons: z.array(buttonSchema).optional(),
});

export const imageCardComponentSchema = baseComponentSchema.extend({
  type: z.literal('image-card'),
  data: imageCardDataSchema,
});

// Type exports
export type ImageCardItem = z.infer<typeof imageCardItemSchema>;
export type ImageCardData = z.infer<typeof imageCardDataSchema>;
export type ImageCardComponent = z.infer<typeof imageCardComponentSchema>;
